import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  message,
  Select,
  Spin,
  Tag,
  Popover,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';

import { News, selectors, uploadNews, uploadNewsRephrasing } from 'state';
import EditableTable from './EditableTable';
import { CATEGORY_LABELS } from '../ScenarioBuilder/CorpusNews/TagFilter';
import NewsBodyModal from './NewsBodyModal';
import {
  fetchNewsPage,
  fetchNewsTags,
  NewsPage,
  NewsPageQuery,
  NewsTagFacet,
  saveNews,
} from './newsApi';
import { API_URL } from 'settings';

const tagLabel = (tag: {
  text: string;
  category: string | null;
  label?: string;
}) => {
  const category = tag.category ? CATEGORY_LABELS[tag.category] : undefined;
  const name = tag.label || tag.text;
  return category ? `${category}: ${name}` : name;
};

const NEWS_CSV_TEMPLATE_CONTENT = [
  'headline,body,timestamp,importance,geography,sectors',
  'Central bank keeps policy rate unchanged,"Officials said inflation is easing, but risks remain elevated.",08/12/2030,2,US,Rates|Macro',
  'Technology shares rise after earnings,A broad rally followed stronger-than-expected quarterly results.,08/13/2030,1,,',
  '',
].join('\n');
const NEWS_CSV_TEMPLATE = `data:text/csv;charset=utf-8,${encodeURIComponent(
  NEWS_CSV_TEMPLATE_CONTENT,
)}`;

const NEWS_REPHRASING_CSV_EXAMPLE_CONTENT = [
  'news_id,rephrased_headline',
  '123,"Central bank holds rates and flags persistent inflation"',
  '456,',
  '',
].join('\n');
const NEWS_REPHRASING_CSV_EXAMPLE = `data:text/csv;charset=utf-8,${encodeURIComponent(
  NEWS_REPHRASING_CSV_EXAMPLE_CONTENT,
)}`;

const readFileAsText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = error => reject(error);
    reader.readAsText(file);
  });

const NewsTab: React.FC = () => {
  const dispatch = useDispatch();

  const token = useSelector(selectors.token);

  const [newsRange, setNewsRange] = useState<(Dayjs | undefined)[]>([
    undefined,
    undefined,
  ]);
  const [showBody, setShowBody] = useState(false);
  const [newsId, setNewsId] = useState(-1);
  // Every importance by default: AI Curated copies sit at a negative importance
  // and an importance-0 default hid them.
  const [importanceFilter, setImportanceFilter] = useState<number | undefined>(
    undefined,
  );
  const [sourceFilter, setSourceFilter] = useState<
    'all' | 'ai_curated' | 'original'
  >('all');
  const [tagFilter, setTagFilter] = useState<number[]>([]);
  const [fileSelected, setFileSelected] = useState(false);
  const [data, setData] = useState(new FormData());
  const [rephrasingFile, setRephrasingFile] = useState<File>();

  useEffect(() => {
    const startDate = Cookies.get('adminNewsStartDate');
    const endDate = Cookies.get('adminNewsEndDate');
    setNewsRange([dayjs(startDate), dayjs(endDate)]);
  }, []);

  useEffect(() => {
    const [startDate, endDate] = newsRange;
    if (!startDate || !endDate) return;
    Cookies.set('adminNewsStartDate', startDate.toISOString());
    Cookies.set('adminNewsEndDate', endDate.toISOString());
  }, [newsRange]);

  const addDaysToRange = (amount: number) => {
    setNewsRange([
      newsRange[0]?.add(amount, 'day'),
      newsRange[1]?.add(amount, 'day'),
    ]);
  };

  // The table reads news a slice at a time, newest first and without bodies; filters run on the server, so a
  // range of any length opens at once and the next slice loads as the table scrolls into view.
  const [rows, setRows] = useState<News[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [next, setNext] = useState<NewsPage['next']>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [tagFacets, setTagFacets] = useState<NewsTagFacet[]>([]);
  const [reload, setReload] = useState(0);
  const request = useRef<AbortController | null>(null);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const rangeStart = newsRange[0]?.toISOString() || '';
  const rangeEnd = newsRange[1]?.toISOString() || '';
  const query: NewsPageQuery | null =
    rangeStart && rangeEnd
      ? {
          start_date: rangeStart,
          end_date: rangeEnd,
          source: sourceFilter,
          ...(typeof importanceFilter === 'number'
            ? { importance: importanceFilter }
            : {}),
          ...(tagFilter.length ? { tag_ids: tagFilter.join(',') } : {}),
        }
      : null;
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    if (!query) return undefined;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setFetching(true);
    setLoadError('');
    setRows([]);
    setNext(null);
    fetchNewsPage({ ...query, limit: 200 }, controller.signal)
      .then(page => {
        if (controller.signal.aborted) return;
        setRows(page.results);
        setTotal(page.total);
        setNext(page.next);
      })
      .catch(reason => {
        if (!controller.signal.aborted)
          setLoadError(reason.message || 'Could not load news.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setFetching(false);
      });
    return () => controller.abort();
  }, [queryKey, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!rangeStart || !rangeEnd) return undefined;
    const controller = new AbortController();
    fetchNewsTags(
      { start_date: rangeStart, end_date: rangeEnd },
      controller.signal,
    )
      .then(page => {
        if (!controller.signal.aborted) setTagFacets(page.results);
      })
      .catch(() => {
        if (!controller.signal.aborted) setTagFacets([]);
      });
    return () => controller.abort();
  }, [rangeStart, rangeEnd, reload]);

  const loadMore = useCallback(() => {
    if (!query || !next || fetching || fetchingMore) return;
    const controller = new AbortController();
    request.current = controller;
    setFetchingMore(true);
    fetchNewsPage({ ...query, ...next, limit: 200 }, controller.signal)
      .then(page => {
        if (controller.signal.aborted) return;
        setRows(current => [...current, ...page.results]);
        setNext(page.next);
      })
      .catch(reason => {
        if (!controller.signal.aborted)
          setLoadError(reason.message || 'Could not load more news.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setFetchingMore(false);
      });
  }, [queryKey, next, fetching, fetchingMore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the next slice while the end of the table is still a screen away.
  useEffect(() => {
    const node = sentinel.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '800px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  const patchRow = (saved: News) => {
    const { body: _body, ...row } = saved;
    setRows(current =>
      current.map(item => (item.id === row.id ? { ...item, ...row } : item)),
    );
  };
  const save = async (change: Partial<News> & { id: number }) => {
    try {
      patchRow(await saveNews(change));
    } catch (reason) {
      message.error(
        reason instanceof Error ? reason.message : 'Could not save the story.',
      );
    }
  };

  // Hundreds of tags can be in a range: the dropdown opens with its own search box.
  const [tagSearch, setTagSearch] = useState('');
  const tagTerm = tagSearch.trim().toLowerCase();
  const tagOptions = tagFacets
    .filter(
      tag =>
        tagFilter.includes(tag.id) ||
        !tagTerm ||
        `${tagLabel(tag)} ${tag.search || ''}`.toLowerCase().includes(tagTerm),
    )
    .map(tag => ({
      value: tag.id,
      label: `${tagLabel(tag)} (${tag.count})`,
    }));

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files as FileList;
    const formData = new FormData();
    if (raw[0]) formData.append('news_file', raw[0], raw[0].name);
    setData(formData);
    setFileSelected(true);
  };

  const uploadHandler = async () => {
    // fetch(`${API_URL}/admin/upload_news/`, {
    //   method: 'POST',
    //   body: data,
    //   headers: {
    //     Authorization: `JWT ${token}`,
    //   },
    // })
    //   .then(Response => {
    //     if (Response.status !== 200) {
    //       message.error('An error has occurred');
    //       (document.getElementById('news_file') as HTMLInputElement).value = '';
    //       setFileSelected(false);
    //     } else {
    //       (document.getElementById('news_file') as HTMLInputElement).value = '';
    //       setFileSelected(false);
    //       message.success('Successful operation');
    //     }
    //   })
    //   .catch(() => {
    //     message.error('An error has occurred');
    //   });

    const file = data.get('news_file') as File;

    const fileContent = await readFileAsText(file);
    console.log('fileContent: ', fileContent);
    console.log('typeof fileContent: ', typeof fileContent);
    dispatch(uploadNews({ news_file: fileContent }));
  };

  const uploadRephrasingHandler = async () => {
    if (!rephrasingFile) return;
    const fileContent = await readFileAsText(rephrasingFile);
    dispatch(uploadNewsRephrasing({ news_rephrasing_file: fileContent }));
  };

  const infoHover = (
    <div>
      <p>Required: headline, body, timestamp, importance</p>
      <p>Optional: geography, sectors</p>
      <p>Timestamp format: MM/DD/YYYY (for example, 08/12/2030)</p>
      <p>Separate multiple sectors with | (for example, Rates|Macro)</p>
    </div>
  );

  return (
    <div>
      <form id={'news_form'}>
        <Popover
          placement={'right'}
          content={infoHover}
          title="News CSV fields"
        >
          <InfoCircleOutlined />
        </Popover>
        <Button
          disabled={!fileSelected}
          style={{ marginLeft: 10 }}
          onClick={uploadHandler}
        >
          Upload
        </Button>
        <input
          style={{ marginLeft: '8px', marginBottom: '12px' }}
          type="file"
          id="news_file"
          name="news_file"
          accept=".csv"
          onChange={e => handleFileSelection(e)}
        />
        <Typography.Link
          style={{ marginLeft: 8 }}
          href={NEWS_CSV_TEMPLATE}
          download="news-upload-template.csv"
        >
          Download CSV template
        </Typography.Link>
        <div>
          <Typography.Text type="secondary">
            Required: headline, body, timestamp, importance. Optional:
            geography, sectors. Separate multiple sectors with |.
          </Typography.Text>
        </div>
      </form>

      <form
        id="news_rephrasing_form"
        style={{ marginTop: 12, marginBottom: 12 }}
      >
        <Typography.Text strong>Upload news rephrasing CSV</Typography.Text>
        <Button
          disabled={!rephrasingFile}
          style={{ marginLeft: 10 }}
          onClick={uploadRephrasingHandler}
        >
          Upload rephrasing
        </Button>
        <input
          aria-label="News rephrasing CSV"
          style={{ marginLeft: 8 }}
          type="file"
          id="news_rephrasing_file"
          name="news_rephrasing_file"
          accept=".csv"
          onChange={event => setRephrasingFile(event.target.files?.[0])}
        />
        <Typography.Link
          style={{ marginLeft: 8 }}
          href={NEWS_REPHRASING_CSV_EXAMPLE}
          download="news-rephrasing-example.csv"
        >
          Download rephrasing CSV example
        </Typography.Link>
        <div>
          <Typography.Text type="secondary">
            Required: news_id, rephrased_headline. A blank rephrased_headline
            clears it and restores the original headline.
          </Typography.Text>
        </div>
      </form>

      <Button onClick={() => addDaysToRange(-1)}>prev day</Button>
      <DatePicker.RangePicker
        format="YYYY-MM-DD"
        value={newsRange as any}
        onChange={range => {
          if (!range) return;
          setNewsRange(range as any);
        }}
      />
      <Button onClick={() => addDaysToRange(1)}>next day</Button>
      <span style={{ marginLeft: '8px' }}>
        <span style={{ marginRight: '8px' }}>Importance=</span>
        <InputNumber
          style={{ width: 50 }}
          value={importanceFilter}
          onChange={value =>
            setImportanceFilter(typeof value === 'number' ? value : undefined)
          }
        />
      </span>
      <span style={{ marginLeft: '8px' }}>
        <span style={{ marginRight: '8px' }}>Source</span>
        <Select
          aria-label="Filter news by source"
          style={{ width: 140 }}
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'ai_curated', label: 'AI Curated' },
            { value: 'original', label: 'Originals' },
          ]}
        />
      </span>
      <span style={{ marginLeft: '8px' }}>
        <span style={{ marginRight: '8px' }}>Tags</span>
        <Select
          mode="multiple"
          aria-label="Filter news by tags"
          placeholder="Any tag in this range"
          style={{ minWidth: 280 }}
          value={tagFilter}
          onChange={setTagFilter}
          options={tagOptions}
          showSearch={false}
          allowClear
          maxTagCount="responsive"
          onOpenChange={open => {
            if (!open) setTagSearch('');
          }}
          notFoundContent="No tag matches your search"
          popupRender={menu => (
            <>
              <Input.Search
                aria-label="Search tags"
                placeholder="Search tags: country, event, indicator, institution, instrument"
                allowClear
                autoFocus
                value={tagSearch}
                onChange={event => setTagSearch(event.target.value)}
                onKeyDown={event => event.stopPropagation()}
                style={{ padding: 8 }}
              />
              {menu}
            </>
          )}
        />
      </span>

      <p style={{ marginTop: '8px' }}>
        Current range: {newsRange[0]?.format('YYYY-MM-DD')}
        {' -> '}
        {newsRange[1]?.format('YYYY-MM-DD')}
      </p>
      <p>
        News: {rows.length}
        {total !== null && total > rows.length ? ` of ${total}` : ''}
        <Button
          size="small"
          style={{ marginLeft: 8 }}
          onClick={() => setReload(value => value + 1)}
        >
          Refresh
        </Button>
      </p>
      {loadError && (
        <Typography.Text type="danger">{loadError}</Typography.Text>
      )}
      <Spin spinning={fetching}>
        <EditableTable
          data={rows}
          onSave={a => {
            void save(a as News);
          }}
          fixedFields={['importance']}
          nonEditableFields={[
            'id',
            'tags',
            'source',
            'original_headline',
            'original_news',
          ]}
          ignoredFields={['body']}
          arrayFields={['sectors']}
          columns={{
            tags: {
              dataIndex: 'tags',
              width: '320px',
              render(
                value: {
                  id: number;
                  text: string;
                  category: string | null;
                  label?: string;
                }[],
              ) {
                return (
                  <span>
                    {(value || []).map(tag => (
                      <Tag key={tag.id} style={{ marginBottom: 4 }}>
                        {tagLabel(tag)}
                      </Tag>
                    ))}
                  </span>
                );
              },
            },
            headline: {
              dataIndex: 'headline',
              width: '800px',
              render(value: any) {
                return (
                  <div
                    style={{
                      wordWrap: 'initial',
                      whiteSpace: 'initial',
                    }}
                  >
                    {value}
                  </div>
                );
              },
              onCell: () => ({
                style: {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
              }),
            },
            rephrased_headline: {
              dataIndex: 'rephrased_headline',
              width: '800px',
              render(value: any) {
                return (
                  <div
                    style={{
                      wordWrap: 'initial',
                      whiteSpace: 'initial',
                    }}
                  >
                    {value}
                  </div>
                );
              },
              onCell: () => ({
                style: {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
              }),
            },
            importance: {
              dataIndex: 'importance',
              width: '85px',
            },
            id: {
              dataIndex: 'id',
              width: '100px',
            },
            timestamp: {
              dataIndex: 'timestamp',
              width: '250px',
            },
            geography: {
              dataIndex: 'geography',
              width: '160px',
            },
            sectors: {
              dataIndex: 'sectors',
              width: '260px',
            },
          }}
          buttons={[
            {
              onClick: (record: News) => {
                void save({ id: record.id, importance: record.importance - 1 });
              },
              title: '-',
            },
            {
              onClick: (record: News) => {
                void save({ id: record.id, importance: record.importance + 1 });
              },
              title: '+',
            },
            {
              onClick: record => {
                setShowBody(true);
                setNewsId(record.id);
              },
              title: 'Body',
            },
          ]}
        />
      </Spin>
      <div ref={sentinel} aria-hidden style={{ height: 1 }} />
      {next && (
        <div style={{ textAlign: 'center', margin: '12px 0' }}>
          <Button loading={fetchingMore} onClick={loadMore}>
            {fetchingMore ? 'Loading more news…' : 'Load more news'}
          </Button>
        </div>
      )}
      {showBody && (
        <NewsBodyModal
          visible={showBody}
          hide={() => setShowBody(false)}
          newsId={newsId}
        />
      )}
    </div>
  );
};

export default NewsTab;
